import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowDownRight, FileText, Wallet, Download, Trash2, Eye, Edit, FileDown, Info, HelpCircle } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import InvoiceModal from '../components/modals/InvoiceModal';
import PaymentModal from '../components/modals/PaymentModal';
import CreditNoteModal from '../components/modals/CreditNoteModal';
import BudgetModal from '../components/modals/BudgetModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import EntityModal from '../components/modals/EntityModal';
import { ClientNotesPanel } from '../components/modals/ClientNotesModal';
import { Budget } from '../types';
import { useAppStore } from '../store';
import {
  getClientAccountBalances,
  getClientOverdueBalances,
  getInvoiceOverdueAmount,
  getInvoiceOverduePercent,
  isInvoiceOverdue,
  isInvoiceOverdueByAmount,
} from '../utils/clientAccountBalance';
import { supabase } from '../lib/supabase';
import { formatCurrency, formatDate, formatCurrencyForCSV } from '../utils/formatters';
import { generateInvoicePDF, generatePaymentPDF, generateBudgetPDF } from '../utils/pdfGenerator';
import { formatArgentinaDate, adjustDateForInput } from '../utils/dateUtils';
import { useToast } from '../hooks/useToast';
import { objectsToCsv, downloadCsv } from '../utils/csvUtils';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import ResizableTh from '../components/shared/ResizableTh';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import productexLogo from '../components/layout/productex-logo.png';

const getRowOverdueAmount = (transaction: {
  type: string;
  voided?: boolean;
  amount?: number;
  overdue_percent?: number;
  overdue_amount?: number | null;
}): number | null => {
  if (transaction.type !== 'invoice' || transaction.voided) return null;
  const total = Number(transaction.amount) || 0;
  if (
    transaction.overdue_amount !== null &&
    transaction.overdue_amount !== undefined &&
    Number.isFinite(Number(transaction.overdue_amount))
  ) {
    return Math.min(total, Math.max(0, Number(transaction.overdue_amount)));
  }
  const pct = Number(transaction.overdue_percent ?? 100);
  return total * (Math.min(100, Math.max(0, pct)) / 100);
};

const TX_DEFAULT_WIDTHS = {
  date: 88,
  due: 88,
  description: 200,
  type: 88,
  amount: 108,
  pct: 118,
  overdueAmount: 108,
  balance: 108,
  actions: 210,
};

const BUDGET_DEFAULT_WIDTHS = {
  date: 88,
  number: 150,
  type: 120,
  valid: 100,
  total: 100,
  actions: 260,
};

const tdCompact = 'px-2 py-1.5 text-xs whitespace-nowrap';
const tdCompactWrap = 'px-2 py-1.5 text-xs';
const btnCompact = '!px-2 !py-0.5 !text-[11px] [&>span]:!mr-1';

const loadImageAsBase64 = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(); return; }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject();
    img.src = url;
  });

// Asegura que el logo renderizado en el PDF sea 100% negro.
// (El asset original puede venir con grises o un tratamiento de color que jsPDF respeta al escalar.)
const loadImageAsBlackBase64 = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(); return; }

      ctx.drawImage(img, 0, 0);
      const { width, height } = canvas;
      const imageData = ctx.getImageData(0, 0, width, height);

      // Forzar todo pixel no-transparente a negro manteniendo alpha
      for (let i = 0; i < imageData.data.length; i += 4) {
        const alpha = imageData.data[i + 3];
        if (alpha > 0) {
          imageData.data[i + 0] = 0; // R
          imageData.data[i + 1] = 0; // G
          imageData.data[i + 2] = 0; // B
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject();
    img.src = url;
  });

const ClientAccount: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [budgetToEdit, setBudgetToEdit] = useState<Budget | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInvoiceType, setSelectedInvoiceType] = useState<'all' | 'A' | 'B' | 'C' | 'X'>('all');
  const [selectedTransactionType, setSelectedTransactionType] = useState<'all' | 'invoice' | 'payment'>('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; type: 'invoice' | 'payment' | 'budget' } | null>(null);
  const [deletedItems, setDeletedItems] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'transactions' | 'budgets'>('transactions');

  // Items de cada factura para mostrar tooltip en la grilla y también exportar a PDF
  const [invoiceItemsByInvoiceId, setInvoiceItemsByInvoiceId] = useState<
    Record<string, { description: string; quantity: number; unit_price: number }[]>
  >({});
  const [isLoadingInvoiceItems, setIsLoadingInvoiceItems] = useState(false);
  
  const { clients, invoices, payments = [], budgets = [], deleteInvoice, deletePayment, deleteBudget, convertBudgetToInvoice, updateClient, updateInvoice, currentUser, fetchBudgets, cleanupObsoleteBudgetTransactions } = useAppStore();
  const { toast, ToastComponent } = useToast();
  const [savingOverdueId, setSavingOverdueId] = useState<string | null>(null);
  const [markingAllOverdue, setMarkingAllOverdue] = useState(false);
  const [itemsTooltip, setItemsTooltip] = useState<{
    invoiceId: string;
    top: number;
    left: number;
  } | null>(null);
  const txCols = useResizableTableColumns('client-account-transactions', TX_DEFAULT_WIDTHS);
  const budgetCols = useResizableTableColumns('client-account-budgets', BUDGET_DEFAULT_WIDTHS);
  
  // Load budgets when component mounts and clean up obsolete transactions
  useEffect(() => {
    const initializeBudgets = async () => {
      await cleanupObsoleteBudgetTransactions();
      await fetchBudgets();
    };
    
    initializeBudgets();
  }, [fetchBudgets, cleanupObsoleteBudgetTransactions]);
  
  // Find the client - moved outside of conditional rendering
  const client = clients.find(c => c.id === id);
  
  // Check if user is admin
  const isAdmin = currentUser?.role?.toLowerCase() === 'administrador';
  const isVendedor = currentUser?.role?.trim().toLowerCase() === 'vendedor';

  const clientInvoiceIds = useMemo(() => {
    if (!id) return [];
    return invoices
      .filter(inv => inv.entity_id === id && inv.entity_type === 'client' && !inv.voided)
      .map(inv => inv.id);
  }, [id, invoices]);

  const clientInvoiceIdsKey = useMemo(() => {
    return [...clientInvoiceIds].sort().join('|');
  }, [clientInvoiceIds]);

  const invoiceCurrencyById = useMemo(() => {
    const map: Record<string, 'ARS' | 'USD'> = {};
    invoices.forEach(inv => {
      if (inv.entity_id === id && inv.entity_type === 'client' && !inv.voided) {
        const curr: 'ARS' | 'USD' = (inv as any).currency === 'USD' ? 'USD' : 'ARS';
        map[inv.id] = curr;
      }
    });
    return map;
  }, [id, invoices]);

  const formatMoney = (amount: number, currency: 'ARS' | 'USD') => {
    const hasCents = Math.abs(amount % 1) > 0.000001;
    const formatted = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);

    // Intl en ARS suele renderizar "$ 1.234". Queremos "$1.234" (sin espacio).
    if (currency === 'ARS') return formatted.replace('$ ', '$');
    return formatted;
  };

  useEffect(() => {
    let cancelled = false;
    const loadInvoiceItems = async () => {
      if (!id || clientInvoiceIds.length === 0) {
        setInvoiceItemsByInvoiceId({});
        return;
      }

      setIsLoadingInvoiceItems(true);
      try {
        const { data, error } = await supabase
          .from('invoice_items')
          .select('invoice_id, description, quantity, unit_price')
          .in('invoice_id', clientInvoiceIds);

        if (error) throw error;

        const grouped: Record<string, { description: string; quantity: number; unit_price: number }[]> = {};
        (data || []).forEach((item: any) => {
          const invoiceId = String(item.invoice_id);
          if (!grouped[invoiceId]) grouped[invoiceId] = [];
          grouped[invoiceId].push({
            description: String(item.description || ''),
            quantity: Number(item.quantity || 0),
            unit_price: Number(item.unit_price || 0),
          });
        });

        if (!cancelled) setInvoiceItemsByInvoiceId(grouped);
      } catch (error) {
        if (!cancelled) setInvoiceItemsByInvoiceId({});
      } finally {
        if (!cancelled) setIsLoadingInvoiceItems(false);
      }
    };

    loadInvoiceItems();
    return () => {
      cancelled = true;
    };
  }, [id, clientInvoiceIdsKey]); // clientInvoiceIdsKey para evitar disparos por referencia de arrays
  
  // Calculate current balance dynamically - moved outside of conditional rendering
  // Balances por moneda
  const { currentBalanceARS, currentBalanceUSD } = useMemo(() => {
    if (!id) return { currentBalanceARS: 0, currentBalanceUSD: 0 };
    const { ars, usd } = getClientAccountBalances(id, invoices, payments);
    return { currentBalanceARS: ars, currentBalanceUSD: usd };
  }, [id, invoices, payments]);

  const { overdueBalanceARS, overdueBalanceUSD } = useMemo(() => {
    if (!id) return { overdueBalanceARS: 0, overdueBalanceUSD: 0 };
    const { ars, usd } = getClientOverdueBalances(id, invoices, payments);
    return { overdueBalanceARS: ars, overdueBalanceUSD: usd };
  }, [id, invoices, payments]);
  
  // Prepare client transactions - moved outside of conditional rendering
  const clientTransactions = useMemo(() => {
    if (!id) return [];
    
    // Get all payments for this client, including those not associated with invoices
    const clientPayments = payments.filter(payment => {
      if (payment.invoice_id) {
        // Payment associated with an invoice
        const invoice = invoices.find(inv => inv.id === payment.invoice_id);
        return invoice?.entity_id === id && invoice?.entity_type === 'client' && !payment.voided;
      } else if (payment.reference === `client:${id}` && !payment.voided) {
        // Account payment for this client
        return true;
      }
      return false;
    });
    
    return [
      ...invoices
        .filter(inv => inv.entity_id === id && inv.entity_type === 'client' && !inv.voided)
        .map(inv => ({
          id: inv.id,
          date: inv.date,
          due_date: inv.due_date,
          timestamp: new Date(inv.date).getTime(),
          createdAt: new Date(inv.created_at).getTime(),
          description: `Factura ${inv.number}`,
          type: 'invoice' as const,
          invoiceType: inv.type,
          amount: inv.total,
          currency: (inv as any).currency === 'USD' ? 'USD' : 'ARS',
          voided: Boolean(inv.voided),
          overdue_percent: getInvoiceOverduePercent(inv),
          overdue_amount: isInvoiceOverdueByAmount(inv)
            ? Number((inv as any).overdue_amount)
            : null,
          is_overdue: isInvoiceOverdue(inv),
          document: () => generateInvoicePDF(inv, client?.name || 'Cliente'),
          invoice_file: inv.invoice_file,
        })),
      ...clientPayments
        .map(pay => {
          const invoice = pay.invoice_id ? invoices.find(inv => inv.id === pay.invoice_id) : null;
          
          // Skip payments that don't belong to this client
          if (!invoice && pay.reference !== `client:${id}`) {
            return null;
          }
          
          const description = pay.invoice_id 
            ? (pay.invoice_type === 'credit_note' 
                ? `Nota de crédito ${pay.reference || ''}` 
                : `Pago ${pay.method}${pay.reference ? ` (${pay.reference})` : ''}`)
            : `Pago a cuenta ${pay.method}${pay.reference ? ` (${pay.reference})` : ''}`;
          
          // Determine display currency and amount for balances
          let transactionCurrency: 'ARS' | 'USD' = (pay as any).currency === 'USD' ? 'USD' : 'ARS';
          let transactionAmount = pay.amount;
          let conversionNote: string | undefined = undefined;
          const formatAmount = (n: number) => {
            const hasCents = Math.abs(n % 1) > 0.000001;
            return new Intl.NumberFormat('es-AR', { minimumFractionDigits: hasCents ? 2 : 0, maximumFractionDigits: hasCents ? 2 : 0 }).format(n);
          };
          const formatRate = (n: number) => {
            const hasCents = Math.abs(n % 1) > 0.000001;
            return new Intl.NumberFormat('es-AR', {
              minimumFractionDigits: hasCents ? 3 : 0,
              maximumFractionDigits: hasCents ? 3 : 0
            }).format(n);
          };
          if (invoice) {
            const invoiceCurr: 'ARS' | 'USD' = (invoice as any).currency === 'USD' ? 'USD' : 'ARS';
            const payCurr: 'ARS' | 'USD' = (pay as any).currency === 'USD' ? 'USD' : 'ARS';
            const rate = (pay as any).exchange_rate as number | undefined;
            transactionCurrency = invoiceCurr;
            if (invoiceCurr !== payCurr && rate && rate > 0) {
              if (invoiceCurr === 'USD' && payCurr === 'ARS') {
                transactionAmount = pay.amount / rate;
                conversionNote = `Pago en ${payCurr} ${formatAmount(pay.amount)} convertido a ${invoiceCurr} ${formatAmount(transactionAmount)} con TC ${formatRate(rate)} ARS/USD`;
              } else if (invoiceCurr === 'ARS' && payCurr === 'USD') {
                transactionAmount = pay.amount * rate;
                conversionNote = `Pago en ${payCurr} ${formatAmount(pay.amount)} convertido a ${invoiceCurr} ${formatAmount(transactionAmount)} con TC ${formatRate(rate)} ARS/USD`;
              }
            }
          }

          return {
            id: pay.id,
            date: pay.date,
            timestamp: new Date(pay.date).getTime(),
            createdAt: new Date(pay.created_at).getTime(),
            description,
            type: 'payment' as const,
            invoiceType: pay.invoice_type,
            amount: transactionAmount,
            currency: transactionCurrency,
            voided: Boolean(pay.voided),
            document: () => generatePaymentPDF(pay, client?.name || 'Cliente'),
            conversionNote,
          };
        }).filter(Boolean), // Remove null entries
    ] as any[];
  }, [id, invoices, payments, client]);

  // Prepare client budgets - separate from transactions
  const clientBudgets = useMemo(() => {
    if (!id) return [];
    
    return budgets
      .filter(budget => budget.entity_id === id && budget.entity_type === 'client')
      .map(budget => ({
        id: budget.id,
        date: budget.date,
        valid_until: budget.valid_until,
        timestamp: new Date(budget.date).getTime(),
        createdAt: new Date(budget.created_at).getTime(),
        description: `Presupuesto ${budget.number}`,
        type: 'budget' as const,
        budgetType: budget.type,
        budgetStatus: budget.status,
        amount: budget.total,
        currency: 'ARS',
        voided: false,
        document: () => null, // TODO: Implementar PDF de presupuesto
      }));
  }, [id, budgets]);
  
  // Filter transactions - moved outside of conditional rendering
  const filteredTransactions = useMemo(() => {
    return clientTransactions.filter(t => {
      const transactionDate = new Date(t.date);
      
      // Filter by date range
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59); // Include the entire end day
        return transactionDate >= start && transactionDate <= end;
      }
      
      // Filter by period
      if (period !== 'all') {
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstDayOfQuarter = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
        
        switch (period) {
          case 'month':
            return transactionDate >= firstDayOfMonth;
          case 'quarter':
            return transactionDate >= firstDayOfQuarter;
          case 'year':
            return transactionDate >= firstDayOfYear;
          default:
            return true;
        }
      }

      return true;
    }).filter(t => {
      // Filter by transaction type
      if (selectedTransactionType !== 'all') {
        return t.type === selectedTransactionType;
      }
      return true;
    }).filter(t => {
      // Filter by invoice type
      if (selectedInvoiceType !== 'all' && t.type === 'invoice') {
        return t.invoiceType === selectedInvoiceType;
      }
      return true;
    }).filter(t => !deletedItems.has(t.id));
  }, [clientTransactions, period, startDate, endDate, selectedTransactionType, selectedInvoiceType, deletedItems]);
  
  // Sort transactions by date (most recent first) and then by creation time if dates are the same
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      // First compare by date
      const dateComparison = b.timestamp - a.timestamp;
      if (dateComparison !== 0) return dateComparison;
      
      // If dates are the same, compare by creation time (most recent first)
      return b.createdAt - a.createdAt;
    });
  }, [filteredTransactions]);
  
  // Calculate totals - moved outside of conditional rendering
  const totalsByCurrency = useMemo(() => {
    const init = { ARS: { invoices: 0, payments: 0 }, USD: { invoices: 0, payments: 0 } } as any;
    filteredTransactions.forEach(t => {
      const c = t.currency === 'USD' ? 'USD' : 'ARS';
      if (t.type === 'invoice') init[c].invoices += t.amount; else init[c].payments += t.amount;
    });
    return init;
  }, [filteredTransactions]);

  // Calculate running balances for each transaction
  const transactionsWithBalances = useMemo(() => {
    // First, sort transactions by date (oldest first) to calculate running balance correctly
    const chronologicalTransactions = [...filteredTransactions].sort((a, b) => {
      // First compare by date
      const dateComparison = a.timestamp - b.timestamp;
      if (dateComparison !== 0) return dateComparison;

      // If dates are the same, compare by creation time (oldest first)
      return a.createdAt - b.createdAt;
    });

    // Calculate initial balance (balance BEFORE the filtered date range)
    let initialBalance: Record<'ARS' | 'USD', number> = { ARS: 0, USD: 0 };

    // If we're filtering by date, we need to calculate what the balance was BEFORE the start date
    if ((startDate && endDate) || period !== 'all') {
      // Determine the actual start date based on period or custom range
      let filterStartDate: Date | null = null;

      if (startDate && endDate) {
        filterStartDate = new Date(startDate);
      } else if (period !== 'all') {
        const now = new Date();
        switch (period) {
          case 'month':
            filterStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
            break;
          case 'quarter':
            filterStartDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
            break;
          case 'year':
            filterStartDate = new Date(now.getFullYear(), 0, 1);
            break;
        }
      }

      // Calculate balance from all transactions BEFORE the filter start date
      if (filterStartDate) {
        clientTransactions.forEach(transaction => {
          const transactionDate = new Date(transaction.date);

          // Only include transactions BEFORE the filter start date
          if (transactionDate < filterStartDate!) {
            const curr: 'ARS' | 'USD' = transaction.currency === 'USD' ? 'USD' : 'ARS';
            if (transaction.type === 'invoice') {
              initialBalance[curr] += transaction.amount;
            } else if (transaction.invoiceType === 'credit_note') {
              initialBalance[curr] -= transaction.amount;
            } else {
              initialBalance[curr] -= transaction.amount;
            }
          }
        });
      }
    }

    // Calculate running balance starting from initial balance
    let running: Record<'ARS' | 'USD', number> = { ...initialBalance };
    const withBalances = chronologicalTransactions.map(transaction => {
      const curr: 'ARS' | 'USD' = transaction.currency === 'USD' ? 'USD' : 'ARS';
      if (transaction.type === 'invoice') running[curr] += transaction.amount;
      else if (transaction.invoiceType === 'credit_note') running[curr] -= transaction.amount;
      else running[curr] -= transaction.amount;

      return {
        ...transaction,
        runningBalance: running[curr]
      };
    });

    // Now sort back to most recent first for display
    return withBalances.sort((a, b) => {
      // First compare by date
      const dateComparison = b.timestamp - a.timestamp;
      if (dateComparison !== 0) return dateComparison;

      // If dates are the same, compare by creation time (most recent first)
      return b.createdAt - a.createdAt;
    });
  }, [filteredTransactions, startDate, endDate, period, clientTransactions]);

  const handleOverdueValueChange = async (
    invoiceId: string,
    value: string,
    mode: 'percent' | 'amount'
  ) => {
    const parsed = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(parsed)) return;
    const current = invoices.find((i) => i.id === invoiceId);
    if (!current) return;

    if (mode === 'percent') {
      const next = Math.min(100, Math.max(0, Math.round(parsed)));
      if (!isInvoiceOverdueByAmount(current) && getInvoiceOverduePercent(current) === next) return;
      setSavingOverdueId(invoiceId);
      try {
        await updateInvoice(invoiceId, { overdue_percent: next, overdue_amount: null } as any);
      } catch (e: any) {
        toast.error(e?.message || 'No se pudo actualizar el % vencido');
      } finally {
        setSavingOverdueId(null);
      }
      return;
    }

    const total = Number(current.total) || 0;
    const nextAmt = Math.min(total, Math.max(0, Math.round(parsed * 100) / 100));
    if (
      isInvoiceOverdueByAmount(current) &&
      Math.abs(getInvoiceOverdueAmount(current) - nextAmt) < 0.005
    ) {
      return;
    }
    const derivedPct =
      total > 0 ? Math.min(100, Math.max(0, Math.round((nextAmt / total) * 100))) : 0;
    setSavingOverdueId(invoiceId);
    try {
      await updateInvoice(invoiceId, {
        overdue_amount: nextAmt,
        overdue_percent: derivedPct,
      } as any);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo actualizar el monto vencido');
    } finally {
      setSavingOverdueId(null);
    }
  };

  const handleOverdueModeToggle = async (
    invoiceId: string,
    nextMode: 'percent' | 'amount'
  ) => {
    const current = invoices.find((i) => i.id === invoiceId);
    if (!current) return;
    const currentMode = isInvoiceOverdueByAmount(current) ? 'amount' : 'percent';
    if (currentMode === nextMode) return;

    const total = Number(current.total) || 0;
    const overdueAmt = getInvoiceOverdueAmount(current);
    setSavingOverdueId(invoiceId);
    try {
      if (nextMode === 'amount') {
        const pct =
          total > 0 ? Math.min(100, Math.max(0, Math.round((overdueAmt / total) * 100))) : 0;
        await updateInvoice(invoiceId, {
          overdue_amount: Math.round(overdueAmt * 100) / 100,
          overdue_percent: pct,
        } as any);
      } else {
        const pct =
          total > 0 ? Math.min(100, Math.max(0, Math.round((overdueAmt / total) * 100))) : 0;
        await updateInvoice(invoiceId, {
          overdue_percent: pct,
          overdue_amount: null,
        } as any);
      }
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo cambiar el modo de vencido');
    } finally {
      setSavingOverdueId(null);
    }
  };

  const handleMarkAllOverdue100 = async () => {
    if (!id) return;
    const toUpdate = invoices.filter(
      (inv) =>
        inv.entity_id === id &&
        inv.entity_type === 'client' &&
        !inv.voided &&
        (getInvoiceOverduePercent(inv) !== 100 || isInvoiceOverdueByAmount(inv))
    );
    if (toUpdate.length === 0) {
      toast.success('Todas las facturas ya están al 100%');
      return;
    }
    setMarkingAllOverdue(true);
    try {
      await Promise.all(
        toUpdate.map((inv) =>
          updateInvoice(inv.id, { overdue_percent: 100, overdue_amount: null } as any)
        )
      );
      toast.success(`${toUpdate.length} factura${toUpdate.length === 1 ? '' : 's'} marcada${toUpdate.length === 1 ? '' : 's'} al 100%`);
    } catch (e: any) {
      toast.error(e?.message || 'Error al marcar al 100%');
    } finally {
      setMarkingAllOverdue(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      if (itemToDelete.type === 'invoice') {
        await deleteInvoice(itemToDelete.id);
      } else if (itemToDelete.type === 'payment') {
        await deletePayment(itemToDelete.id);
      } else if (itemToDelete.type === 'budget') {
        await deleteBudget(itemToDelete.id);
      }
      
      // Mark item as deleted in local state
      setDeletedItems(prev => new Set([...prev, itemToDelete.id]));
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  // Generate and download PDF report
  const generatePDF = async () => {
    if (!client) return;
    
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    
    let periodText = 'Período: ';
    if (period === 'all') {
      periodText += 'Todo el historial';
    } else if (period === 'custom' && startDate && endDate) {
      periodText += `${formatArgentinaDate(startDate)} - ${formatArgentinaDate(endDate)}`;
    } else {
      periodText += period === 'month' ? 'Este mes' :
                   period === 'quarter' ? 'Este trimestre' : 'Este año';
    }

    const pt = (size: number) => size * 0.3528;
    const titleSize = 16;
    const periodSize = 9;
    const titleAscent = pt(titleSize) * 0.72;
    const periodAscent = pt(periodSize) * 0.72;
    const lineGap = 1.6;
    const blockH = titleAscent + lineGap + periodAscent;

    const marginX = 14;
    const logoW = 38;
    let logoH = logoW * (18 / 45);
    let logoData: string | null = null;

    try {
      logoData = await loadImageAsBlackBase64(productexLogo);
      const img = new Image();
      img.src = logoData;
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
      if (img.width && img.height) logoH = logoW * (img.height / img.width);
    } catch {
      logoData = null;
      logoH = pt(12);
    }

    const rowTop = 8;
    const rowH = Math.max(logoH, blockH);
    const logoY = rowTop + (rowH - logoH) / 2;
    const blockTop = rowTop + (rowH - blockH) / 2;
    const titleBaseline = blockTop + titleAscent;
    const periodBaseline = titleBaseline + lineGap + periodAscent;
    const textX = marginX + logoW + 6;

    if (logoData) {
      doc.addImage(logoData, 'PNG', marginX, logoY, logoW, logoH);
    } else {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('PRODUCTEX', marginX, titleBaseline);
    }

    doc.setFontSize(titleSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(`Cuenta de Cliente: ${client.name}`, textX, titleBaseline);

    doc.setFontSize(periodSize);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(90);
    doc.text(periodText, textX, periodBaseline);
    doc.setTextColor(0);

    const saldoY = rowTop + rowH + 7;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(
      `Saldo vencido: ARS ${formatCurrency(overdueBalanceARS)}  |  USD ${formatCurrency(overdueBalanceUSD)}`,
      marginX,
      saldoY
    );

    const sectionY = saldoY + 7;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Movimientos de Cuenta', marginX, sectionY);
    
    const tableColumn = ['Fecha', 'Venc.', 'Descripción', 'Tipo', 'Monto', '%/$', 'Saldo venc.', 'Saldo'];

    const invoiceIdsInTable = transactionsWithBalances
      .filter((t: any) => t.type === 'invoice')
      .map((t: any) => t.id);

    const invoiceItemsForExport: Record<string, { description: string; quantity: number; unit_price: number }[]> = {
      ...invoiceItemsByInvoiceId,
    };

    const missingInvoiceIds = invoiceIdsInTable.filter(invId => !invoiceItemsForExport[invId]);
    if (missingInvoiceIds.length) {
      try {
        const { data, error } = await supabase
          .from('invoice_items')
          .select('invoice_id, description, quantity, unit_price')
          .in('invoice_id', missingInvoiceIds);

        if (!error) {
          (data || []).forEach((item: any) => {
            const invoiceId = String(item.invoice_id);
            if (!invoiceItemsForExport[invoiceId]) invoiceItemsForExport[invoiceId] = [];
            invoiceItemsForExport[invoiceId].push({
              description: String(item.description || ''),
              quantity: Number(item.quantity || 0),
              unit_price: Number(item.unit_price || 0),
            });
          });
        }
      } catch {
        // Si falla, igualmente exportamos la descripción base sin items
      }
    }
    
    const tableRows = transactionsWithBalances.map(transaction => {
      const overdueAmt = getRowOverdueAmount(transaction);
      return [
        formatArgentinaDate(transaction.date),
        transaction.type === 'invoice' && transaction.due_date
          ? formatArgentinaDate(transaction.due_date)
          : '—',
        transaction.type === 'invoice'
          ? (() => {
              const base = transaction.description + (transaction.voided ? ' (Anulado)' : '');
              const items = invoiceItemsForExport[transaction.id] || [];
              const itemsText = items.length
                ? items.map(i => {
                    const curr = invoiceCurrencyById[transaction.id] || 'ARS';
                    return `${i.description} - x${i.quantity} - ${formatMoney(i.unit_price, curr)}`;
                  }).join('\n')
                : '';
              return base + (itemsText ? `\n${itemsText}` : '');
            })()
          : transaction.description + (transaction.voided ? ' (Anulado)' : ''),
        transaction.voided
          ? 'Anulado'
          : transaction.type === 'invoice'
            ? 'Factura'
            : transaction.invoiceType === 'credit_note'
              ? 'N. Crédito'
              : 'Pago',
        (transaction.type === 'payment' ? '-' : '') + formatCurrency(transaction.amount),
        transaction.type === 'invoice' && !transaction.voided
          ? transaction.overdue_amount !== null && transaction.overdue_amount !== undefined
            ? `${formatCurrency(Number(transaction.overdue_amount))} $`
            : `${transaction.overdue_percent ?? 100}%`
          : '—',
        overdueAmt !== null ? formatCurrency(overdueAmt) : '—',
        formatCurrency(transaction.runningBalance),
      ];
    });
    
    (doc as any).autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: sectionY + 4,
      theme: 'plain',
      showHead: 'everyPage',
      margin: { left: 10, right: 10, top: 16 },
      pageBreak: 'auto',
      rowPageBreak: 'auto',
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        textColor: [0, 0, 0],
        fontStyle: 'normal',
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        cellPadding: 2.5,
        lineWidth: 0,
      },
      bodyStyles: {
        textColor: [0, 0, 0],
        lineWidth: 0.1,
        lineColor: [220, 220, 220],
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 22 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 22 },
        4: { cellWidth: 28, halign: 'right' },
        5: { cellWidth: 14, halign: 'center' },
        6: { cellWidth: 28, halign: 'right' },
        7: { cellWidth: 28, halign: 'right' },
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
    });
    
    const pageCount = (doc as any).internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(80);
    doc.setFont('helvetica', 'normal');
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Generado el ${formatArgentinaDate(new Date().toISOString())}`, 10, doc.internal.pageSize.height - 8);
      doc.text(`Página ${i} de ${pageCount}`, pageWidth - 30, doc.internal.pageSize.height - 8);
    }
    
    doc.save(`cuenta-cliente-${client.name.replace(/\s+/g, '-')}.pdf`);
  };

  const exportCSV = () => {
    if (!client) return;

    const rows = transactionsWithBalances.map((transaction) => {
      const overdueAmt = getRowOverdueAmount(transaction);
      const tipo = transaction.voided
        ? 'Anulado'
        : transaction.type === 'invoice'
          ? 'Factura'
          : transaction.invoiceType === 'credit_note'
            ? 'Nota de crédito'
            : 'Pago';

      return {
        Fecha: formatArgentinaDate(transaction.date),
        Vencimiento:
          transaction.type === 'invoice' && transaction.due_date
            ? formatArgentinaDate(transaction.due_date)
            : '',
        Descripción: transaction.description + (transaction.voided ? ' (Anulado)' : ''),
        Tipo: tipo,
        Moneda: transaction.currency || 'ARS',
        Monto: formatCurrencyForCSV(
          transaction.type === 'payment' ? -Math.abs(transaction.amount) : transaction.amount
        ),
        'Vencido %/$':
          transaction.type === 'invoice' && !transaction.voided
            ? transaction.overdue_amount !== null && transaction.overdue_amount !== undefined
              ? `${formatCurrencyForCSV(Number(transaction.overdue_amount))} $`
              : `${transaction.overdue_percent ?? 100}%`
            : '',
        'Saldo vencido':
          overdueAmt !== null ? formatCurrencyForCSV(overdueAmt) : '',
        Saldo: formatCurrencyForCSV(transaction.runningBalance),
      };
    });

    const headers = [
      'Fecha',
      'Vencimiento',
      'Descripción',
      'Tipo',
      'Moneda',
      'Monto',
      'Vencido %/$',
      'Saldo vencido',
      'Saldo',
    ];

    const summaryRows = [
      {},
      { Fecha: 'RESUMEN', Descripción: client.name },
      {
        Fecha: 'Facturación ARS',
        Monto: formatCurrencyForCSV(totalsByCurrency.ARS.invoices),
      },
      {
        Fecha: 'Facturación USD',
        Monto: formatCurrencyForCSV(totalsByCurrency.USD.invoices),
      },
      {
        Fecha: 'Pagos ARS',
        Monto: formatCurrencyForCSV(totalsByCurrency.ARS.payments),
      },
      {
        Fecha: 'Pagos USD',
        Monto: formatCurrencyForCSV(totalsByCurrency.USD.payments),
      },
      {
        Fecha: 'Saldo actual ARS',
        Monto: formatCurrencyForCSV(currentBalanceARS),
      },
      {
        Fecha: 'Saldo actual USD',
        Monto: formatCurrencyForCSV(currentBalanceUSD),
      },
      {
        Fecha: 'Saldo vencido ARS',
        Monto: formatCurrencyForCSV(overdueBalanceARS),
      },
      {
        Fecha: 'Saldo vencido USD',
        Monto: formatCurrencyForCSV(overdueBalanceUSD),
      },
    ];

    const csv = objectsToCsv([...rows, ...summaryRows], headers);
    downloadCsv(csv, `cuenta-cliente-${client.name.replace(/\s+/g, '-')}.csv`);
  };
  
  // If client not found, show error message
  if (!client) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-12rem)]">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Cliente no encontrado</h2>
          <p className="text-gray-600">El cliente que buscas no existe o no tienes acceso.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{client.name}</h2>
          <p className="text-sm text-gray-500">{client.email}</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col lg:flex-row justify-between gap-3">
          {/* Primary Actions Row */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={generatePDF}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md bg-black text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors w-full sm:w-auto"
              title="Exportar PDF"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Exportar PDF
            </button>

            <button
              onClick={exportCSV}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors w-full sm:w-auto"
              title="Exportar CSV"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </button>
            
            <Button 
              variant="outline" 
              icon={<Edit className="h-4 w-4" />}
              onClick={() => setIsEditModalOpen(true)}
              className="w-full sm:w-auto"
            >
              Editar Cliente
            </Button>
          </div>
          
          {/* Secondary Actions Row - Aligned to right */}
          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <Button 
              variant="primary" 
              icon={<FileText className="h-4 w-4" />}
              onClick={() => setIsInvoiceModalOpen(true)}
              className="w-full sm:w-auto"
            >
              Nueva Factura
            </Button>
            
            <Button 
              variant="secondary" 
              icon={<FileText className="h-4 w-4" />}
              onClick={() => {
                setBudgetToEdit(undefined);
                setIsBudgetModalOpen(true);
              }}
              className="w-full sm:w-auto"
            >
              Nuevo Presupuesto
            </Button>
            
            <Button 
              variant="outline" 
              icon={<Wallet className="h-4 w-4" />}
              onClick={() => setIsPaymentModalOpen(true)}
              className="w-full sm:w-auto"
            >
              Registrar Pago
            </Button>
            
            <Button 
              variant="outline" 
              icon={<FileText className="h-4 w-4" />}
              onClick={() => setIsCreditNoteModalOpen(true)}
              className="w-full sm:w-auto"
            >
              Nota de Crédito
            </Button>
          </div>
        </div>
      </div>
      
      <div className={`grid grid-cols-1 gap-3 ${isVendedor ? 'md:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
        {!isVendedor && (
        <Card className="!p-3">
          <CardContent>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Facturación</p>
                <div className="mt-0.5">
                  {totalsByCurrency.ARS.invoices !== 0 && (
                    <p className="text-sm font-semibold text-indigo-600 leading-tight">{formatCurrency(totalsByCurrency.ARS.invoices)} <span className="text-[10px] text-gray-400">ARS</span></p>
                  )}
                  {totalsByCurrency.USD.invoices !== 0 && (
                    <p className="text-sm font-semibold text-indigo-600 leading-tight">{formatCurrency(totalsByCurrency.USD.invoices)} <span className="text-[10px] text-gray-400">USD</span></p>
                  )}
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-indigo-500 shrink-0" />
            </div>
          </CardContent>
        </Card>
        )}
        
        {!isVendedor && (
        <Card className="!p-3">
          <CardContent>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Pagos</p>
                <div className="mt-0.5">
                  {totalsByCurrency.ARS.payments !== 0 && (
                    <p className="text-sm font-semibold text-emerald-600 leading-tight">{formatCurrency(totalsByCurrency.ARS.payments)} <span className="text-[10px] text-gray-400">ARS</span></p>
                  )}
                  {totalsByCurrency.USD.payments !== 0 && (
                    <p className="text-sm font-semibold text-emerald-600 leading-tight">{formatCurrency(totalsByCurrency.USD.payments)} <span className="text-[10px] text-gray-400">USD</span></p>
                  )}
                </div>
              </div>
              <ArrowDownRight className="h-4 w-4 text-emerald-500 shrink-0" />
            </div>
          </CardContent>
        </Card>
        )}
        
        <Card className="!p-3">
          <CardContent>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Saldo actual</p>
                <div className="mt-0.5">
                  <p className={`text-sm font-semibold leading-tight ${currentBalanceARS >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(currentBalanceARS)} <span className="text-[10px] text-gray-400">ARS</span></p>
                  <p className={`text-sm font-semibold leading-tight ${currentBalanceUSD >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(currentBalanceUSD)} <span className="text-[10px] text-gray-400">USD</span></p>
                </div>
              </div>
              {(currentBalanceARS >= 0 && currentBalanceUSD >= 0) ? (
                <ArrowUpRight className="h-4 w-4 text-emerald-500 shrink-0" />
              ) : (
                <ArrowDownRight className="h-4 w-4 text-red-500 shrink-0" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="!p-3">
          <CardContent>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Saldo vencido</p>
                <div className="mt-0.5">
                  <p className={`text-sm font-semibold leading-tight ${overdueBalanceARS > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(overdueBalanceARS)} <span className="text-[10px] text-gray-400">ARS</span>
                  </p>
                  <p className={`text-sm font-semibold leading-tight ${overdueBalanceUSD > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {formatCurrency(overdueBalanceUSD)} <span className="text-[10px] text-gray-400">USD</span>
                  </p>
                </div>
              </div>
              {(overdueBalanceARS > 0 || overdueBalanceUSD > 0) ? (
                <ArrowDownRight className="h-4 w-4 text-red-500 shrink-0" />
              ) : (
                <ArrowUpRight className="h-4 w-4 text-emerald-500 shrink-0" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {isAdmin && <ClientNotesPanel clientId={client.id} />}
      
      <Card>
        <CardHeader
          title="Movimientos de Cuenta"
          action={
            !isVendedor ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllOverdue100}
                disabled={markingAllOverdue}
              >
                {markingAllOverdue ? 'Marcando…' : 'Marcar todo al 100%'}
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('transactions')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'transactions'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Transacciones
                </button>
                <button
                  onClick={() => setActiveTab('budgets')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'budgets'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Presupuestos ({clientBudgets.length})
                </button>
              </nav>
            </div>
          </div>
          {activeTab === 'transactions' && (
            <>
              <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <Select
                  label="Período"
                  value={period}
                  onChange={(value) => {
                    setPeriod(value);
                    setStartDate('');
                    setEndDate('');
                  }}
                  options={[
                    { value: 'all', label: 'Todo' },
                    { value: 'month', label: 'Este mes' },
                    { value: 'quarter', label: 'Este trimestre' },
                    { value: 'year', label: 'Este año' },
                    { value: 'custom', label: 'Personalizado' },
                  ]}
                />
                
                {period === 'custom' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Desde
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Hasta
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                  </>
                )}

                <Select
                  label="Tipo de Factura"
                  value={selectedInvoiceType}
                  onChange={(value) => setSelectedInvoiceType(value as typeof selectedInvoiceType)}
                  options={[
                    { value: 'all', label: 'Todas' },
                    { value: 'A', label: 'Factura A' },
                    { value: 'B', label: 'Factura B' },
                    { value: 'C', label: 'Factura C' },
                    { value: 'X', label: 'Factura X' },
                  ]}
                />

                <Select
                  label="Tipo de Movimiento"
                  value={selectedTransactionType}
                  onChange={(value) => setSelectedTransactionType(value as typeof selectedTransactionType)}
                  options={[
                    { value: 'all', label: 'Todos' },
                    { value: 'invoice', label: 'Facturas' },
                    { value: 'payment', label: 'Pagos' },
                  ]}
                />
              </div>
              
              <div className="overflow-x-auto">
              <table
                className="table-fixed divide-y divide-gray-200"
                style={{
                  width: Object.values(txCols.widths).reduce((a, b) => a + b, 0),
                  minWidth: '100%',
                }}
              >
                <thead className="bg-gray-50">
                  <tr>
                    <ResizableTh width={txCols.getWidth('date')} onResizeStart={(e) => txCols.onResizeStart('date', e)}>
                      Fecha
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('due')} onResizeStart={(e) => txCols.onResizeStart('due', e)}>
                      Vencimiento
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('description')} onResizeStart={(e) => txCols.onResizeStart('description', e)}>
                      Descripción
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('type')} onResizeStart={(e) => txCols.onResizeStart('type', e)}>
                      Tipo
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('amount')} onResizeStart={(e) => txCols.onResizeStart('amount', e)} align="right">
                      Monto
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('pct')} onResizeStart={(e) => txCols.onResizeStart('pct', e)} align="center">
                      Vencido %/$
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('overdueAmount')} onResizeStart={(e) => txCols.onResizeStart('overdueAmount', e)} align="right">
                      Saldo vencido
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('balance')} onResizeStart={(e) => txCols.onResizeStart('balance', e)} align="right">
                      Saldo
                    </ResizableTh>
                    <ResizableTh width={txCols.getWidth('actions')} onResizeStart={(e) => txCols.onResizeStart('actions', e)} align="center">
                      Acciones
                    </ResizableTh>
                  </tr>
                </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactionsWithBalances.map((transaction) => (
                  <tr 
                    key={transaction.id} 
                    className={`hover:bg-gray-50 ${
                      transaction.voided ? 'bg-red-50' : ''
                    }`}
                  >
                    <td className={`${tdCompact} text-gray-500`}>
                      {formatArgentinaDate(transaction.date)}
                    </td>
                    <td className={`${tdCompact} text-gray-500`}>
                      {transaction.type === 'invoice' && transaction.due_date ? 
                        formatArgentinaDate(transaction.due_date) : 
                        '-'}
                    </td>
                    <td className={`${tdCompactWrap} text-gray-900`}>
                      {transaction.type === 'invoice' ? (
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 truncate">
                            <span>{transaction.description}</span>
                            {transaction.voided && (
                              <span className="ml-2 text-red-600 text-xs">(Anulado)</span>
                            )}
                          </div>

                          <span
                            className="inline-flex items-center shrink-0"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setItemsTooltip({
                                invoiceId: transaction.id,
                                top: rect.bottom + 6,
                                left: Math.min(rect.left, window.innerWidth - 360),
                              });
                            }}
                            onMouseLeave={() => setItemsTooltip(null)}
                          >
                            <HelpCircle className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                          </span>
                        </div>
                      ) : (
                        <>
                          {transaction.description}
                          {transaction.voided && (
                            <span className="ml-2 text-red-600 text-xs">(Anulado)</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className={tdCompact}>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[11px] ${
                          transaction.voided
                            ? 'bg-red-100 text-red-800'
                            : transaction.type === 'invoice'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {transaction.voided
                          ? 'Anulado'
                          : transaction.type === 'invoice'
                          ? 'Factura'
                          : 'Pago'}
                      </span>
                    </td>
                    <td className={`${tdCompact} font-medium text-right`}>
                      <span
                        className={
                          transaction.type === 'payment' && transaction.invoiceType !== 'credit_note'
                            ? 'text-emerald-600 dark:text-emerald-400'
                          : transaction.type === 'payment' && transaction.invoiceType === 'credit_note'
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-blue-600 dark:text-blue-400'
                        }
                      >
                        <span className="inline-flex items-center gap-1 relative group">
                          {transaction.type === 'payment' && transaction.invoiceType !== 'credit_note' && '-'}
                          {transaction.type === 'payment' && transaction.invoiceType === 'credit_note' && '-'}
                          {formatCurrency(transaction.amount)} <span className="text-[10px] text-gray-500">({transaction.currency || 'ARS'})</span>
                          {transaction.conversionNote && (
                            <>
                              <Info className="inline-block ml-1 h-3.5 w-3.5 text-gray-400" />
                              <span className="pointer-events-none absolute -top-8 right-0 z-20 hidden whitespace-nowrap rounded bg-black px-2 py-1 text-white text-xs shadow-lg group-hover:block">
                                {transaction.conversionNote}
                              </span>
                            </>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className={`${tdCompact} text-center`}>
                      {transaction.type === 'invoice' && !transaction.voided ? (
                        (() => {
                          const byAmount =
                            transaction.overdue_amount !== null &&
                            transaction.overdue_amount !== undefined;
                          const mode: 'percent' | 'amount' = byAmount ? 'amount' : 'percent';
                          const displayValue = byAmount
                            ? Number(transaction.overdue_amount)
                            : (transaction.overdue_percent ?? 100);
                          return (
                            <div className="inline-flex items-center gap-0.5">
                              <input
                                type="number"
                                min={0}
                                max={mode === 'percent' ? 100 : transaction.amount}
                                step={mode === 'percent' ? 1 : 0.01}
                                defaultValue={displayValue}
                                key={`${transaction.id}-${mode}-${displayValue}`}
                                disabled={isVendedor || savingOverdueId === transaction.id}
                                onBlur={(e) =>
                                  handleOverdueValueChange(transaction.id, e.target.value, mode)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    (e.target as HTMLInputElement).blur();
                                  }
                                }}
                                className={`w-16 px-1 py-0.5 text-xs text-center border rounded-l-md focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                                  transaction.is_overdue
                                    ? 'border-red-300 bg-red-50 text-red-700'
                                    : 'border-gray-300 bg-white text-gray-700'
                                } disabled:opacity-60`}
                                title={
                                  mode === 'percent'
                                    ? 'Porcentaje del monto que cuenta como vencido'
                                    : 'Monto en $ que cuenta como vencido'
                                }
                              />
                              <div className="inline-flex rounded-r-md border border-l-0 border-gray-300 overflow-hidden">
                                <button
                                  type="button"
                                  disabled={isVendedor || savingOverdueId === transaction.id}
                                  onClick={() => handleOverdueModeToggle(transaction.id, 'percent')}
                                  className={`px-1 py-0.5 text-[10px] leading-none disabled:opacity-60 ${
                                    mode === 'percent'
                                      ? 'bg-indigo-600 text-white'
                                      : 'bg-white text-gray-500 hover:bg-gray-50'
                                  }`}
                                  title="Editar en %"
                                >
                                  %
                                </button>
                                <button
                                  type="button"
                                  disabled={isVendedor || savingOverdueId === transaction.id}
                                  onClick={() => handleOverdueModeToggle(transaction.id, 'amount')}
                                  className={`px-1 py-0.5 text-[10px] leading-none disabled:opacity-60 ${
                                    mode === 'amount'
                                      ? 'bg-indigo-600 text-white'
                                      : 'bg-white text-gray-500 hover:bg-gray-50'
                                  }`}
                                  title="Editar en $"
                                >
                                  $
                                </button>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className={`${tdCompact} font-medium text-right`}>
                      {(() => {
                        const overdueAmt = getRowOverdueAmount(transaction);
                        if (overdueAmt === null) {
                          return <span className="text-gray-300">—</span>;
                        }
                        return (
                          <span className={transaction.is_overdue ? 'text-red-600' : 'text-gray-500'}>
                            {formatCurrency(overdueAmt)}{' '}
                            <span className="text-[10px] text-gray-500">({transaction.currency || 'ARS'})</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`${tdCompact} font-medium text-right`}>
                      <span className={transaction.runningBalance > 0 ? 'text-emerald-600' : 'text-red-600'}>
                        <span className="inline-flex items-center gap-1 relative group">
                          {formatCurrency(transaction.runningBalance)} <span className="text-[10px] text-gray-500">({transaction.currency || 'ARS'})</span>
                          {transaction.conversionNote && (
                            <>
                              <Info className="inline-block ml-1 h-3.5 w-3.5 text-gray-400" />
                              <span className="pointer-events-none absolute -top-8 right-0 z-20 hidden whitespace-nowrap rounded bg-black px-2 py-1 text-white text-xs shadow-lg group-hover:block">
                                {transaction.conversionNote}
                              </span>
                            </>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className={`${tdCompact} text-center`}>
                      <div className="flex justify-center gap-1 flex-wrap">
                        {transaction.type === 'invoice' && transaction.invoice_file && (
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Eye className="h-4 w-4" />}
                            onClick={() => window.open(transaction.invoice_file, '_blank')}
                          >
                            Ver Factura
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className={btnCompact}
                          icon={<Download className="h-3 w-3" />}
                          onClick={transaction.document}
                        >
                          PDF
                        </Button>
                        {transaction.type === 'budget' && transaction.budgetStatus !== 'converted' && (
                          <Button
                            variant="primary"
                            size="sm"
                            icon={<FileText className="h-4 w-4" />}
                            onClick={async () => {
                              try {
                                await convertBudgetToInvoice(transaction.id);
                                toast.success(
                                  'Presupuesto convertido',
                                  'El presupuesto se ha convertido exitosamente a factura'
                                );
                              } catch (error) {
                                console.error('Error converting budget to invoice:', error);
                                toast.error(
                                  'Error en la conversión',
                                  'No se pudo convertir el presupuesto a factura'
                                );
                              }
                            }}
                          >
                            Convertir a Factura
                          </Button>
                        )}
                        {!transaction.voided && (
                          <Button
                            variant="danger"
                            size="sm"
                            className={btnCompact}
                            icon={<Trash2 className="h-3 w-3" />}
                            onClick={() => {
                              setItemToDelete({
                                id: transaction.id,
                                type: transaction.type
                              });
                              setDeleteModalOpen(true);
                            }}
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            </>
          )}
          
          {activeTab === 'budgets' && (
            <div className="overflow-x-auto">
              <table
                className="table-fixed divide-y divide-gray-200"
                style={{
                  width: Object.values(budgetCols.widths).reduce((a, b) => a + b, 0),
                  minWidth: '100%',
                }}
              >
                <thead className="bg-gray-50">
                  <tr>
                    <ResizableTh width={budgetCols.getWidth('date')} onResizeStart={(e) => budgetCols.onResizeStart('date', e)}>
                      Fecha
                    </ResizableTh>
                    <ResizableTh width={budgetCols.getWidth('number')} onResizeStart={(e) => budgetCols.onResizeStart('number', e)}>
                      Número
                    </ResizableTh>
                    <ResizableTh width={budgetCols.getWidth('type')} onResizeStart={(e) => budgetCols.onResizeStart('type', e)}>
                      Tipo
                    </ResizableTh>
                    <ResizableTh width={budgetCols.getWidth('valid')} onResizeStart={(e) => budgetCols.onResizeStart('valid', e)}>
                      Válido hasta
                    </ResizableTh>
                    <ResizableTh width={budgetCols.getWidth('total')} onResizeStart={(e) => budgetCols.onResizeStart('total', e)} align="right">
                      Total
                    </ResizableTh>
                    <ResizableTh width={budgetCols.getWidth('actions')} onResizeStart={(e) => budgetCols.onResizeStart('actions', e)} align="center">
                      Acciones
                    </ResizableTh>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clientBudgets.map((budget) => (
                    <tr key={budget.id} className="hover:bg-gray-50">
                      <td className={`${tdCompact} text-gray-500`}>
                        {formatArgentinaDate(budget.date)}
                      </td>
                      <td className={`${tdCompact} text-gray-900 truncate`}>
                        {budget.description}
                      </td>
                      <td className={tdCompact}>
                        <span className="px-1.5 py-0.5 rounded-full text-[11px] bg-blue-100 text-blue-800">
                          Presupuesto {budget.budgetType}
                        </span>
                      </td>
                      <td className={`${tdCompact} text-gray-500`}>
                        {budget.valid_until ? formatArgentinaDate(budget.valid_until) : '-'}
                      </td>
                      <td className={`${tdCompact} font-medium text-right`}>
                        <span className="text-blue-600 dark:text-blue-400">
                          ${budget.amount.toFixed(2)}
                        </span>
                      </td>
                      <td className={`${tdCompact} text-center`}>
                        <div className="flex justify-center gap-1 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            className={btnCompact}
                            icon={<Download className="h-3 w-3" />}
                            onClick={async () => {
                              try {
                                const fullBudget = budgets.find(b => b.id === budget.id);
                                if (fullBudget) {
                                  await generateBudgetPDF(fullBudget, client?.name || 'Cliente');
                                }
                              } catch (error) {
                                console.error('Error generating budget PDF:', error);
                              }
                            }}
                          >
                            PDF
                          </Button>
                          {budget.budgetStatus !== 'converted' && (
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Edit className="h-4 w-4" />}
                              onClick={() => {
                                const fullBudget = budgets.find(b => b.id === budget.id);
                                if (fullBudget) {
                                  setBudgetToEdit(fullBudget);
                                  setIsBudgetModalOpen(true);
                                }
                              }}
                            >
                              Editar
                            </Button>
                          )}
                          {budget.budgetStatus !== 'converted' && (
                            <Button
                              variant="primary"
                              size="sm"
                              icon={<FileText className="h-4 w-4" />}
                              onClick={async () => {
                                try {
                                  await convertBudgetToInvoice(budget.id);
                                  toast.success(
                                    'Presupuesto convertido',
                                    'El presupuesto se ha convertido exitosamente a factura'
                                  );
                                } catch (error) {
                                  console.error('Error converting budget to invoice:', error);
                                  toast.error(
                                    'Error en la conversión',
                                    'No se pudo convertir el presupuesto a factura'
                                  );
                                }
                              }}
                            >
                              Facturar
                            </Button>
                          )}
                          <Button
                            variant="danger"
                            size="sm"
                            className={btnCompact}
                            icon={<Trash2 className="h-3 w-3" />}
                            onClick={() => {
                              setItemToDelete({
                                id: budget.id,
                                type: 'budget'
                              });
                              setDeleteModalOpen(true);
                            }}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {clientBudgets.length === 0 && (
                    <tr>
                      <td colSpan={6} className={`${tdCompact} text-center text-gray-500`}>
                        No hay presupuestos para este cliente
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <InvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
        entity_id={client.id}
        entity_type="client"
        entityName={client.name}
      />
      
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        entityId={client.id}
        entityName={client.name}
        entityType="client"
      />
      
      <CreditNoteModal
        isOpen={isCreditNoteModalOpen}
        onClose={() => setIsCreditNoteModalOpen(false)}
        entityId={client.id}
        entityName={client.name}
        entityType="client"
      />

      <BudgetModal
        isOpen={isBudgetModalOpen}
        onClose={() => {
          setIsBudgetModalOpen(false);
          setBudgetToEdit(undefined);
        }}
        entity_id={client.id}
        entity_type="client"
        entityName={client.name}
        budgetToEdit={budgetToEdit}
      />

      <EntityModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        entityType="client"
        onSave={async (updatedClient) => {
          if (id) {
            await updateClient(id, updatedClient);
          }
          setIsEditModalOpen(false);
        }}
        initialData={client}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDelete}
        title={`Eliminar ${itemToDelete?.type === 'invoice' ? 'Factura' : itemToDelete?.type === 'payment' ? 'Pago' : 'Presupuesto'}`}
        message={`¿Estás seguro de que deseas eliminar ${itemToDelete?.type === 'invoice' ? 'esta factura' : itemToDelete?.type === 'payment' ? 'este pago' : 'este presupuesto'}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />

      {ToastComponent}

      {itemsTooltip &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[9999] max-w-[340px] whitespace-normal rounded bg-black px-2 py-1.5 text-xs text-white shadow-lg"
            style={{ top: itemsTooltip.top, left: Math.max(8, itemsTooltip.left) }}
          >
            {isLoadingInvoiceItems ? (
              'Cargando...'
            ) : (invoiceItemsByInvoiceId[itemsTooltip.invoiceId] || []).length ? (
              <div className="space-y-1">
                {(invoiceItemsByInvoiceId[itemsTooltip.invoiceId] || []).map((it, idx) => (
                  <div key={`${itemsTooltip.invoiceId}-${idx}`} className="leading-snug">
                    {it.description} - x{it.quantity} -{' '}
                    {formatMoney(it.unit_price, invoiceCurrencyById[itemsTooltip.invoiceId] || 'ARS')}
                  </div>
                ))}
              </div>
            ) : (
              'Sin items'
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ClientAccount;